import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import apis from "../../../../api/api.actions";
import Snackbar from "../../../../components/snackbar/snackbar";
import "./style.css";

const PASSWORD_RULES_MESSAGE =
  "Password must include uppercase, lowercase, number, and special character.";

function ResetPasswordModal({ show, onHide, token }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [closeOnSnackOk, setCloseOnSnackOk] = useState(false);

  useEffect(() => {
    if (!show) return;
    setCurrentPassword("");
    setNewPassword("");
    setNewPasswordConfirmation("");
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowNewPasswordConfirmation(false);
    setSubmitting(false);
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
    setCloseOnSnackOk(false);
  }, [show]);

  const openSnack = (message, isError, shouldCloseOnOk = false) => {
    setSnackMessage(message || "");
    setIsSnackError(Boolean(isError));
    setCloseOnSnackOk(Boolean(shouldCloseOnOk));
    setSnackOpen(true);
  };

  const isPasswordValid = (password) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialCharacter = /[^A-Za-z0-9]/.test(password);

    return hasUppercase && hasLowercase && hasNumber && hasSpecialCharacter;
  };

  const handleSubmit = async () => {
    if (submitting) return;

    if (!currentPassword || !newPassword || !newPasswordConfirmation) {
      openSnack("Please fill all fields.", true);
      return;
    }
    if (newPassword !== newPasswordConfirmation) {
      openSnack("New password and confirmation do not match.", true);
      return;
    }
    if (!isPasswordValid(newPassword)) {
      openSnack(PASSWORD_RULES_MESSAGE, true);
      return;
    }

    try {
      setSubmitting(true);
      const res = await apis.createData(
        "company/update-password",
        {
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirmation: newPasswordConfirmation,
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );

      const successMessage =
        res?.message || res?.data?.message || "Password updated successfully.";

      await Swal.fire({
        icon: "success",
        title: "Password Reset Successful",
        text: successMessage,
        confirmButtonText: "OK",
      });

      onHide?.();
    } catch (err) {
      console.error("Update password failed:", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Update password failed. Please try again.";
      openSnack(String(msg), true, false);
    } finally {
      setSubmitting(false);
    }
  };

  const snackbarClose = (isError) => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
    if (!isError && closeOnSnackOk) onHide?.();
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        dialogClassName="reset-pass-modal"
        contentClassName="modal-content-reset"
        backdrop="static"
      >
        <div className="p-[20px]">
          <div className="">
            <button
              type="button"
              className="reset-pass-close"
              onClick={onHide}
              aria-label="Close"
              disabled={submitting}
            >
              ×
            </button>

            <div className="reset-pass-header">Reset Password</div>

            <div className="reset-pass-body d-flex justify-content-center align-items-center flex-column">
              <div>
                <img src="/resources/img/login/login-avatar.png" alt="" />
              </div>
              <div className="reset-pass-label">
                Enter your current password and new password
              </div>

              <div className="w-100 reset-pass-fields">
                <div className="reset-pass-field mb-2">
                  <label className="reset-pass-field-label">
                    Current Password <span className="reset-pass-required">*</span>
                  </label>
                  <div className="reset-pass-input-wrap">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      className="form-control reset-pass-input"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      className="reset-pass-eye"
                      onClick={() => setShowCurrentPassword((prev) => !prev)}
                      aria-label={showCurrentPassword ? "Hide current password" : "Show current password"}
                    >
                      <i className={`fa ${showCurrentPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                    </button>
                  </div>
                </div>

                <div className="reset-pass-field mb-2">
                  <label className="reset-pass-field-label">
                    New Password <span className="reset-pass-required">*</span>
                  </label>
                  <div className="reset-pass-input-wrap">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      className="form-control reset-pass-input"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="reset-pass-eye"
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                    >
                      <i className={`fa ${showNewPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                    </button>
                  </div>
                  <div className="reset-pass-hint">{PASSWORD_RULES_MESSAGE}</div>
                </div>

                <div className="reset-pass-field mb-1">
                  <label className="reset-pass-field-label">
                    Confirm New Password <span className="reset-pass-required">*</span>
                  </label>
                  <div className="reset-pass-input-wrap">
                    <input
                      type={showNewPasswordConfirmation ? "text" : "password"}
                      className="form-control reset-pass-input"
                      value={newPasswordConfirmation}
                      onChange={(e) => setNewPasswordConfirmation(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="reset-pass-eye"
                      onClick={() => setShowNewPasswordConfirmation((prev) => !prev)}
                      aria-label={showNewPasswordConfirmation ? "Hide confirm password" : "Show confirm password"}
                    >
                      <i className={`fa ${showNewPasswordConfirmation ? "fa-eye-slash" : "fa-eye"}`}></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="reset-pass-action">
              <button
                className="reset-pass-button"
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />
    </>
  );
}

export default ResetPasswordModal;
